import React, { useState, useEffect } from 'react';
import { User, Settings, Shield, Key, Download, X } from 'lucide-react';

function UserSettings({ user, isOpen, onClose, api, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    number: user?.number || '',
    password: '',
    error: '',
    success: '',
    updated: false, // Track if form was recently updated
  });
  const [twoFactorSetup, setTwoFactorSetup] = useState({
    secret: '',
    qrCode: '',
    backupCodes: [],
    token: '',
    error: '',
    success: '',
    isEnabled: user?.is2FAEnabled || false,
  });

  useEffect(() => {
    if (user) {
      setProfileForm(prev => ({
        name: user.name || '',
        email: user.email || '',
        number: user.number || '',
        password: '',
        error: '',
        success: prev.success || '', // Preserve existing success message
        updated: prev.updated || false, // Preserve existing updated state
      }));
      setTwoFactorSetup(prev => ({
        ...prev,
        isEnabled: user.is2FAEnabled || false,
      }));
    }
  }, [user]);

  const handleProfileUpdate = async e => {
    e.preventDefault();
    try {
      console.log('🔄 Starting profile update...');
      console.log('Profile data to send:', {
        name: profileForm.name,
        email: profileForm.email,
        number: profileForm.number,
        password: profileForm.password || undefined,
      });

      const response = await api.updateProfile({
        name: profileForm.name,
        email: profileForm.email,
        number: profileForm.number,
        password: profileForm.password || undefined,
      });

      console.log('✅ Profile update response:', response);

      // Update the parent component's user state
      if (onUserUpdate && response.user) {
        console.log('🔄 Updating parent user state with:', response.user);
        onUserUpdate(response.user);
      }

      setProfileForm(prev => {
        const newState = {
          ...prev,
          success: '✅ Profile updated successfully!',
          error: '',
          updated: true,
        };
        console.log('🎉 Setting success state:', newState);
        return newState;
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        console.log('⏰ Clearing success message after 3 seconds');
        setProfileForm(prev => ({ ...prev, success: '', updated: false }));
      }, 3000);
    } catch (error) {
      console.error('❌ Profile update error:', error);
      setProfileForm(prev => ({ ...prev, error: error.message, success: '' }));
    }
  };

  const handleProfileChange = e => {
    const { name, value } = e.target;
    console.log('📝 Profile form change:', { name, value, currentSuccess: profileForm.success });
    setProfileForm(prev => ({ ...prev, [name]: value, error: '', success: '', updated: false }));
  };

  const handleSetup2FA = async () => {
    try {
      const response = await api.setup2FA();
      setTwoFactorSetup(prev => ({
        ...prev,
        secret: response.secret,
        qrCode: response.qrCode,
        backupCodes: response.backupCodes,
        error: '',
        success: '',
      }));
    } catch (error) {
      setTwoFactorSetup(prev => ({ ...prev, error: error.message }));
    }
  };

  const handleEnable2FA = async e => {
    e.preventDefault();
    if (!twoFactorSetup.token) {
      setTwoFactorSetup(prev => ({
        ...prev,
        error: 'Please enter the 6-digit code from your authenticator app.',
      }));
      return;
    }

    try {
      await api.enable2FA(twoFactorSetup.secret, twoFactorSetup.token, twoFactorSetup.backupCodes);
      setTwoFactorSetup(prev => ({
        ...prev,
        isEnabled: true,
        success: '2FA has been enabled successfully!',
        error: '',
      }));
    } catch (error) {
      setTwoFactorSetup(prev => ({ ...prev, error: error.message }));
    }
  };

  const handleDisable2FA = async e => {
    e.preventDefault();
    if (!twoFactorSetup.token) {
      setTwoFactorSetup(prev => ({
        ...prev,
        error: 'Please enter the 6-digit code from your authenticator app to disable 2FA.',
      }));
      return;
    }

    try {
      await api.disable2FA(twoFactorSetup.token);
      setTwoFactorSetup(prev => ({
        ...prev,
        isEnabled: false,
        secret: '',
        qrCode: '',
        backupCodes: [],
        token: '',
        success: '2FA has been disabled successfully!',
        error: '',
      }));
    } catch (error) {
      setTwoFactorSetup(prev => ({ ...prev, error: error.message }));
    }
  };

  const handleTokenChange = e => {
    setTwoFactorSetup(prev => ({ ...prev, token: e.target.value, error: '', success: '' }));
  };

  const downloadBackupCodes = () => {
    const codes = twoFactorSetup.backupCodes.join('\n');
    const blob = new Blob([codes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Settings className="w-6 h-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Account Settings</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'profile'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'security'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Security
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={profileForm.name}
                  onChange={handleProfileChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    profileForm.updated ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileForm.email}
                  onChange={handleProfileChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    profileForm.updated ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                  required
                />
              </div>

              <div>
                <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="number"
                  name="number"
                  value={profileForm.number}
                  onChange={handleProfileChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    profileForm.updated ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={profileForm.password}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                />
              </div>

              {profileForm.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {profileForm.error}
                </div>
              )}

              {profileForm.success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2 animate-pulse">
                  <span className="text-green-600">✅</span>
                  <span className="font-medium">{profileForm.success}</span>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Update Profile
                </button>
              </div>
            </form>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* 2FA Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {twoFactorSetup.isEnabled
                        ? '2FA is currently enabled for your account.'
                        : 'Add an extra layer of security to your account with 2FA.'}
                    </p>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      twoFactorSetup.isEnabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {twoFactorSetup.isEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>

              {/* 2FA Setup/Management */}
              {!twoFactorSetup.isEnabled ? (
                <div className="space-y-4">
                  {!twoFactorSetup.secret ? (
                    <div>
                      <button
                        onClick={handleSetup2FA}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        <span>Enable Two-Factor Authentication</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Step 1: Scan QR Code</h4>
                        <p className="text-sm text-blue-800 mb-3">
                          Use your authenticator app (Google Authenticator, Authy, etc.) to scan
                          this QR code:
                        </p>
                        {twoFactorSetup.qrCode && (
                          <div className="flex justify-center mb-3">
                            <img
                              src={twoFactorSetup.qrCode}
                              alt="2FA QR Code"
                              className="w-32 h-32"
                            />
                          </div>
                        )}
                        <p className="text-xs text-blue-700">
                          Or manually enter this secret:{' '}
                          <code className="bg-blue-100 px-1 rounded">{twoFactorSetup.secret}</code>
                        </p>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-medium text-yellow-900 mb-2">
                          Step 2: Save Backup Codes
                        </h4>
                        <p className="text-sm text-yellow-800 mb-3">
                          Save these backup codes in a safe place. You can use them to access your
                          account if you lose your authenticator device:
                        </p>
                        <div className="bg-white border border-yellow-300 rounded p-3 mb-3">
                          <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                            {twoFactorSetup.backupCodes.map((code, index) => (
                              <div key={index} className="text-center">
                                {code}
                              </div>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={downloadBackupCodes}
                          className="flex items-center space-x-2 px-3 py-1 bg-yellow-600 text-white hover:bg-yellow-700 rounded text-sm transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          <span>Download Backup Codes</span>
                        </button>
                      </div>

                      <form onSubmit={handleEnable2FA} className="space-y-4">
                        <div>
                          <label
                            htmlFor="token"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Step 3: Enter 6-digit code from your authenticator app
                          </label>
                          <input
                            type="text"
                            id="token"
                            value={twoFactorSetup.token}
                            onChange={handleTokenChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest"
                            placeholder="000000"
                            maxLength="6"
                            pattern="[0-9]{6}"
                          />
                        </div>

                        {twoFactorSetup.error && (
                          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {twoFactorSetup.error}
                          </div>
                        )}

                        {twoFactorSetup.success && (
                          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                            {twoFactorSetup.success}
                          </div>
                        )}

                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() =>
                              setTwoFactorSetup(prev => ({
                                ...prev,
                                secret: '',
                                qrCode: '',
                                backupCodes: [],
                                token: '',
                                error: '',
                                success: '',
                              }))
                            }
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
                          >
                            Enable 2FA
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-900 mb-2">
                      Disable Two-Factor Authentication
                    </h4>
                    <p className="text-sm text-red-800 mb-3">
                      If you disable 2FA, your account will be less secure. You'll need to enter a
                      code from your authenticator app to disable it.
                    </p>

                    <form onSubmit={handleDisable2FA} className="space-y-3">
                      <div>
                        <label
                          htmlFor="disable-token"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Enter 6-digit code from your authenticator app
                        </label>
                        <input
                          type="text"
                          id="disable-token"
                          value={twoFactorSetup.token}
                          onChange={handleTokenChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-center text-lg tracking-widest"
                          placeholder="000000"
                          maxLength="6"
                          pattern="[0-9]{6}"
                        />
                      </div>

                      {twoFactorSetup.error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                          {twoFactorSetup.error}
                        </div>
                      )}

                      {twoFactorSetup.success && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                          {twoFactorSetup.success}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                      >
                        Disable 2FA
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
